import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { useSidebar } from "../../context/SidebarContext";
import {
  Shield,
  Building2,
  Users,
  MapPin,
  Route,
  ClipboardList,
  DollarSign,
  LogOut,
  LayoutDashboard,
  Menu,
  X,
  UserCheck,
  Radio,
  Calendar,
  Settings,
  Clock,
  ChevronDown,
  ChevronRight,
  BarChart3,
  CalendarDays,
  UserCog,
  Wrench,
  ChevronsLeft,
  ChevronsRight,
  ChevronsDown,
  ChevronsUp,
} from "lucide-react";
import { Button } from "../ui/button";
import { useState } from "react";
import { cn } from "../../lib/utils";
import { LanguageSelector } from "../LanguageSelector";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";

export const Sidebar = () => {
  const { user, logout, isAdmin, canManageCompanies, canManage } = useAuth();
  const { t } = useTranslation();
  const { isCollapsed, setIsCollapsed } = useSidebar();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  
  // Estado para controlar qué grupos están expandidos (todos expandidos por defecto)
  const [expandedGroups, setExpandedGroups] = useState({
    operation: true,
    planning: true,
    surveillance: true,
    administration: true,
    system: true,
  });

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  const expandAllGroups = () => {
    setExpandedGroups({
      operation: true,
      planning: true,
      surveillance: true,
      administration: true,
      system: true,
    });
  };

  const collapseAllGroups = () => {
    setExpandedGroups({
      operation: false,
      planning: false,
      surveillance: false,
      administration: false,
      system: false,
    });
  };

  const allGroupsExpanded = Object.values(expandedGroups).every(expanded => expanded);

  // 📊 Operación
  const operationItems = [
    {
      to: "/dashboard",
      icon: LayoutDashboard,
      label: t("nav.dashboard"),
      show: true,
    },
    {
      to: "/active-guards",
      icon: Radio,
      label: t("nav.activeGuards"),
      show: canManage(),
    },
    {
      to: "/punch-records",
      icon: Clock,
      label: t("nav.punchRecords"),
      show: canManage(),
    },
    {
      to: "/guard-records",
      icon: UserCheck,
      label: t("nav.guardRecords"),
      show: canManage(),
    },
    {
      to: "/patrol-logs",
      icon: ClipboardList,
      label: t("nav.patrolLogs"),
      show: canManage(),
    },
  ];

  // 📅 Planificación
  const planningItems = [
    {
      to: "/work-schedules",
      icon: Clock,
      label: t("nav.workSchedules"),
      show: canManage(),
    },
    {
      to: "/guard-schedule-calendar",
      icon: Calendar,
      label: t("nav.guardScheduleCalendar"),
      show: canManage(),
    },
    {
      to: "/holidays",
      icon: Calendar,
      label: t("nav.holidays"),
      show: canManage(),
    },
  ];

  // 👮‍♂️ Vigilancia
  const surveillanceItems = [
    {
      to: "/checkpoints",
      icon: MapPin,
      label: t("nav.checkpoints"),
      show: canManage(),
    },
    {
      to: "/routes",
      icon: Route,
      label: t("nav.routes"),
      show: canManage(),
    },
  ];

  // 💰 Administración
  const administrationItems = [
    {
      to: "/payroll",
      icon: DollarSign,
      label: t("nav.payroll"),
      show: canManage(),
    },
    {
      to: "/companies",
      icon: Building2,
      label: t("nav.companies"),
      show: canManageCompanies(),
    },
    {
      to: "/users",
      icon: Users,
      label: t("nav.users"),
      show: canManage(),
    },
  ];

  // ⚙️ Sistema
  const systemItems = [
    {
      to: "/company-settings",
      icon: Settings,
      label: t("nav.settings"),
      show: canManage(),
    },
  ];

  const renderNavGroup = (title, icon: any, items, groupKey) => {
    const filteredItems = items.filter((item) => item.show);
    if (filteredItems.length === 0) return null;

    const Icon = icon;
    const isExpanded = expandedGroups[groupKey];

    const handleGroupClick = () => {
      if (isCollapsed) {
        // Si está contraído, expandir el menú y el grupo
        setIsCollapsed(false);
        setExpandedGroups(prev => ({ ...prev, [groupKey]: true }));
      } else {
        // Si está expandido, solo toggle del grupo
        toggleGroup(groupKey);
      }
    };

    return (
      <Collapsible
        open={isExpanded && !isCollapsed}
        onOpenChange={(open) => {
          if (isCollapsed) {
            // Si está contraído, expandir el menú y el grupo
            setIsCollapsed(false);
            setExpandedGroups(prev => ({ ...prev, [groupKey]: true }));
          } else {
            // Si está expandido, actualizar el estado del grupo
            setExpandedGroups(prev => ({ ...prev, [groupKey]: open }));
          }
        }}
        className="mb-2"
      >
        <CollapsibleTrigger 
          className={cn(
            "w-full flex items-center justify-between px-4 py-2 rounded-lg hover:bg-slate-100 transition-colors",
            isCollapsed && "justify-center"
          )}
        >
          <div className="flex items-center gap-2 text-slate-700 font-medium">
            <Icon className="h-4 w-4" />
            {!isCollapsed && <span>{title}</span>}
          </div>
          {!isCollapsed && (
            isExpanded ? (
              <ChevronDown className="h-4 w-4 text-slate-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-slate-500" />
            )
          )}
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-1 space-y-1">
          {filteredItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={(e) => {
                setIsOpen(false);
                // Si está contraído, expandir el menú al hacer click en una sub-opción
                if (isCollapsed) {
                  e.preventDefault();
                  setIsCollapsed(false);
                  // Expandir el grupo también
                  setExpandedGroups(prev => ({ ...prev, [groupKey]: true }));
                  // Navegar después de un pequeño delay para que la animación se complete
                  setTimeout(() => {
                    navigate(item.to);
                  }, 300);
                }
              }}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-4 py-2 rounded-lg font-medium transition-colors text-sm",
                  isActive
                    ? "bg-primary text-white"
                    : "text-slate-600 hover:bg-slate-100",
                  isCollapsed ? "justify-center ml-0" : "ml-4"
                )
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              title={isCollapsed ? item.label : undefined}
            >
              <item.icon className="h-4 w-4 flex-shrink-0" />
              {!isCollapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </CollapsibleContent>
      </Collapsible>
    );
  };

  return (
    <>
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 lg:hidden"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="mobile-menu-toggle"
      >
        {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </Button>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 h-full sidebar-gradient border-r border-slate-200 z-50 transition-all duration-300 lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full",
          isCollapsed ? "w-[80px]" : "w-[280px]"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo y control de colapso */}
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center gap-3 justify-between">
              <div className={cn("flex items-center gap-3", isCollapsed && "justify-center w-full")}>
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                {!isCollapsed && (
                  <div>
                    <h1 className="font-heading text-xl font-bold text-slate-900">
                      Sentinel Pro
                    </h1>
                    <p className="text-xs text-slate-500">{t("app.securityManagement")}</p>
                  </div>
                )}
              </div>
              {!isCollapsed && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setIsCollapsed(true)}
                  title="Contraer menú"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
              )}
            </div>
            {!isCollapsed && (
              <div className="mt-4">
                <LanguageSelector />
              </div>
            )}
            {isCollapsed && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setIsCollapsed(false)}
                  title="Expandir menú"
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {!isCollapsed && (
              <div className="mb-3 pb-3 border-b border-slate-200">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-xs text-slate-600 hover:text-slate-900"
                  onClick={allGroupsExpanded ? collapseAllGroups : expandAllGroups}
                >
                  {allGroupsExpanded ? (
                    <>
                      <ChevronsUp className="h-3 w-3 mr-2" />
                      {t("nav.collapseAll")}
                    </>
                  ) : (
                    <>
                      <ChevronsDown className="h-3 w-3 mr-2" />
                      {t("nav.expandAll")}
                    </>
                  )}
                </Button>
              </div>
            )}
            {renderNavGroup("Operación", BarChart3, operationItems, "operation")}
            {renderNavGroup("Planificación", CalendarDays, planningItems, "planning")}
            {renderNavGroup("Vigilancia", UserCog, surveillanceItems, "surveillance")}
            {renderNavGroup("Administración", DollarSign, administrationItems, "administration")}
            {renderNavGroup("Sistema", Wrench, systemItems, "system")}
          </nav>

          {/* User info */}
          <div className="p-4 border-t border-slate-100">
            <div className={cn("flex items-center gap-3 mb-4", isCollapsed && "justify-center")}>
              <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-slate-600">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              {!isCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                </div>
              )}
            </div>
            <Button
              variant="outline"
              className={cn(
                "w-full gap-2",
                isCollapsed ? "justify-center px-2" : "justify-start"
              )}
              onClick={logout}
              data-testid="logout-btn"
              title={isCollapsed ? t("nav.logout") : undefined}
            >
              <LogOut className="h-4 w-4 flex-shrink-0" />
              {!isCollapsed && <span>{t("nav.logout")}</span>}
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

